import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useInterviewers } from '../context/InterviewerContext';
import type { Database } from '../lib/supabase';

type Interviewer = Database['public']['Tables']['interviewers']['Row'];
type InterviewerUpdate = Partial<Database['public']['Tables']['interviewers']['Update']>;

export default function EditInterviewer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getInterviewer, updateInterviewer } = useInterviewers();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<InterviewerUpdate>({
    name: '',
    company: '',
    status: 'active',
    vapi_assistant_id: '',
    vapi_api_key: '',
    phone_number: ''
  });

  const interviewer = id ? getInterviewer(Number(id)) : undefined;

  useEffect(() => {
    if (interviewer) {
      setFormData({
        name: interviewer.name,
        company: interviewer.company,
        status: interviewer.status,
        vapi_assistant_id: interviewer.vapi_assistant_id,
        vapi_api_key: interviewer.vapi_api_key,
        phone_number: interviewer.phone_number || ''
      });
    } else {
      navigate('/');
      toast.error('Interviewer not found');
    }
  }, [interviewer, navigate]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name?.trim() || !formData.company?.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      await updateInterviewer(Number(id), formData);
      navigate('/');
      toast.success('Interviewer updated successfully');
    } catch (error) {
      console.error('Error updating interviewer:', error);
      toast.error('Failed to update interviewer');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!interviewer) {
    return null;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Edit AI Interviewer</h1>
        <p className="mt-2 text-sm text-gray-600">
          Update your AI interviewer configuration and settings.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div>
            <label 
              htmlFor="name" 
              className="block text-sm font-medium text-gray-900 mb-1.5"
            >
              Interviewer Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={handleInputChange}
              className="input"
              required
            />
          </div>

          <div>
            <label 
              htmlFor="company" 
              className="block text-sm font-medium text-gray-900 mb-1.5"
            >
              Company Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="company"
              value={formData.company}
              onChange={handleInputChange}
              className="input"
              required
            />
          </div>

          <div>
            <label 
              htmlFor="status" 
              className="block text-sm font-medium text-gray-900 mb-1.5"
            >
              Status
            </label>
            <select
              id="status"
              value={formData.status}
              onChange={handleInputChange}
              className="input"
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </div>
          <div>
            <label 
              htmlFor="vapi_assistant_id" 
              className="block text-sm font-medium text-gray-900 mb-1.5"
            >
              Vapi Assistant ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="vapi_assistant_id"
              value={formData.vapi_assistant_id}
              onChange={handleInputChange}
              className="input"
              required
            />
          </div>
          
          <div>
            <label 
              htmlFor="vapi_api_key" 
              className="block text-sm font-medium text-gray-900 mb-1.5"
            >
              Vapi API Key <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="vapi_api_key"
              value={formData.vapi_api_key}
              onChange={handleInputChange}
              className="input"
              required
            />
          </div>
          
          <div>
            <label 
              htmlFor="phone_number" 
              className="block text-sm font-medium text-gray-900 mb-1.5"
            >
              Phone Number
            </label>
            <input
              type="tel"
              id="phone_number"
              value={formData.phone_number || ''}
              onChange={handleInputChange}
              className="input"
              placeholder="+1 (555) 123-4567"
            />
            <p className="mt-1 text-sm text-gray-500">
              Optional: Users can call this number to start an interview
            </p>
          </div>
         </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}